import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

/**
 * The i18n scaffold. ENGLISH ONLY.
 *
 * 🔴 MALAYALAM HAS BEEN REMOVED FROM THE PRODUCT, NOT PAUSED BEHIND A FLAG.
 * The `ml` copy tables, the Malayalam curriculum prompts, the language toggles,
 * the Malayalam webfonts and the `[lang='ml']` leading overrides are all gone
 * from the tree. This is a deliberate departure from the previous pause, which
 * was expressed as routing precisely so it would cost nothing to reverse.
 *
 * ⚠️ THE WAY BACK IS GIT, NOT A FLAG. Everything deleted is recoverable from
 * history — the ML copy was removed in one commit, so `git revert` restores the
 * tables wholesale. If Malayalam returns, widen `Lang` back to `'en' | 'ml'`
 * and the type errors will lead to every site that needs a second variant.
 *
 * 🔴 `Localized<T>` IS STILL THE SHAPE COPY TABLES USE, and that is deliberate.
 * Collapsing it to a bare `T` would have meant editing every copy table in the
 * repo a second time and would have thrown away the seam a second language
 * needs. As `Record<'en', T>` it costs one key and keeps the seam open — and
 * because TypeScript rejects excess properties on object literals, an `ml:` key
 * added back by hand is now a COMPILE ERROR rather than dead weight nobody
 * notices.
 *
 * Voice remains PRE-RECORDED, NEVER TTS (ADR-011) — that rule was never about
 * Malayalam, it is about a child who cannot read.
 */
export type Lang = "en";

export const LANGS: Record<Lang, { label: string; htmlLang: string }> = {
  en: { label: "English", htmlLang: "en" },
};

/* ──────────────────────────────────────────────────────────── context ── */

interface I18nValue {
  lang: Lang;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  /*
   * ⚠️ THE `lang` ATTRIBUTE IS STILL SET, EVEN THOUGH IT IS NOW CONSTANT.
   * It is what tells a screen reader which voice to use and what lets the
   * browser hyphenate; dropping it because the app is monolingual would be a
   * silent accessibility regression, not a simplification.
   */
  useEffect(() => {
    document.documentElement.lang = LANGS.en.htmlLang;
  }, []);

  const value = useMemo<I18nValue>(() => ({ lang: "en" }), []);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLang(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useLang must be used inside <I18nProvider>");
  return ctx;
}

/**
 * Pick the current language's copy from a copy table.
 *
 * 🔴 STILL A LOOKUP RATHER THAN `dict.en` AT EVERY CALL SITE. Hundreds of
 * components call this; routing them all through one function is what makes
 * adding a language later a change to this file rather than to all of them.
 */
export type Localized<T> = Record<Lang, T>;

export function useCopy<T>(dict: Localized<T>): T {
  const { lang } = useLang();
  return dict[lang];
}
